// Vendor Imports
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { useEffect, useRef, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { ForgeTab, ForgeTabBar } from '@tylertech/forge-react';
import { type ITabBarChangeEventData } from '@tylertech/forge';

import './colorBuckets.scss';

// Project Imports
import { I18n } from 'common/i18n';
import { EMPTY_TRANSPARENT_COLOR } from 'common/authoring_workflow/constants';
import { getSiteAppearanceColorPickerHexCodes } from 'common/visualizations/helpers/SiteAppearanceColors';
import { Key } from 'common/types/keyboard/key';

// Constants
const scope = 'shared.components.color_picker';
const DEFAULT_TAB = 'default';
const CUSTOM_TAB = 'custom';
// prettier-ignore
const DEFAULT_COLOR_PALETTE = [
  '#f0f2ff', '#eff8fb', '#eff8fb', '#f7f7f7', '#fdeddd', '#f6eef7', '#feffc8', '#fdebe1', '#fdffac',
  '#bed7e8', '#b5e3e2', '#b5cce5', '#cccccc', '#fac07e', '#bfc8e3', '#a2deb2', '#f8b2b8', '#fad04b',
  '#71abd9', '#6ac5a3', '#8f92c9', '#969696', '#f98d27', '#6da7d2', '#4cb6c6', '#f45ca1', '#f98d27',
  '#3d7ec0', '#31a75a', '#894baa', '#636363', '#e25200', '#2a919a', '#387bbb', '#c3008c', '#ec3001',
  '#1e489f', '#067126', '#80007f', '#252525', '#a33200', '#0f6f59', '#2d2298', '#79007a', '#ba001e',
];

export const ColorBuckets = ({
  bucketRevealDirection,
  onClickBucket,
  palette = DEFAULT_COLOR_PALETTE,
  selectedColor,
  selectedTab,
  showingBuckets,
}: {
  bucketRevealDirection?: string;
  onClickBucket: (color: string) => void;
  palette?: string[];
  selectedColor: string | null;
  selectedTab?: string; // used by tests only
  showingBuckets?: boolean;
}) => {
  const [activeColor, setActiveColor] = useState<string | null>(selectedColor);
  const [activeTab, setActiveTab] = useState(selectedTab || DEFAULT_TAB);
  const siteAppearanceHexCodes = getSiteAppearanceColorPickerHexCodes();

  const colorBucketsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveColor(selectedColor);
  }, [selectedColor]);

  /**
   * When the buckets open (hidden→shown, never on mount), move focus onto the selected swatch,
   * or the first swatch when none is selected.
   */
  const wasShowingBuckets = useRef(showingBuckets);
  useEffect(() => {
    if (!wasShowingBuckets.current && showingBuckets) {
      const container = colorBucketsRef.current;
      const swatchToFocus =
        container?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ??
        container?.querySelector<HTMLElement>('[role="option"]');
      swatchToFocus?.focus();
    }
    wasShowingBuckets.current = showingBuckets;
  });

  const onChangeActiveColor = ({
    event,
    applyOnSixDigitRgbOnly,
  }: {
    event: SyntheticEvent<HTMLInputElement>;
    applyOnSixDigitRgbOnly: boolean;
  }) => {
    const nextColor = event.currentTarget.value;
    setActiveColor(nextColor);
    const regex = applyOnSixDigitRgbOnly ? /(^#[0-9A-F]{6}$)/i : /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i;

    if (regex.test(nextColor)) {
      onClickBucket(nextColor);
    }
  };

  const onKeyUpColorBucket = (color: string, event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter || event.key === Key.Space) {
      onClickBucket(color);
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === Key.Escape) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onKeyUpHexInput = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === Key.Enter) {
      onChangeActiveColor({ event, applyOnSixDigitRgbOnly: false });
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const renderColorBucket = (color: string, key: number) => {
    const isSelectedColor = color === selectedColor;

    return (
      <div
        key={key}
        id={color}
        data-testid={`color-bucket-${key}`}
        tabIndex={0}
        role="option"
        onClick={() => onClickBucket(color)}
        onKeyUp={(event) => onKeyUpColorBucket(color, event)}
        style={{ backgroundColor: color }}
        aria-selected={isSelectedColor}
        aria-label={`${I18n.t('pickable_color', { scope })} ${color}`}
        className={classNames(
          'color-bucket',
          { 'selected-color': isSelectedColor },
          { 'color-none': color === EMPTY_TRANSPARENT_COLOR },
        )}
      />
    );
  };

  const renderTabs = () => (
    <ForgeTabBar
      activeTab={activeTab === CUSTOM_TAB ? 1 : 0}
      clustered
      underline
      on-forge-tab-bar-change={(event: CustomEvent<ITabBarChangeEventData>) =>
        setActiveTab(event.detail.index === 1 ? CUSTOM_TAB : DEFAULT_TAB)
      }
    >
      <ForgeTab>{I18n.t('default', { scope })}</ForgeTab>
      <ForgeTab>{I18n.t('custom', { scope })}</ForgeTab>
    </ForgeTabBar>
  );

  const hasSiteAppearanceHexCodes = !isEmpty(siteAppearanceHexCodes);
  const colors = hasSiteAppearanceHexCodes && activeTab === CUSTOM_TAB ? siteAppearanceHexCodes : palette;

  const bucketContainerClassName = classNames('color-buckets-container', {
    hidden: !showingBuckets,
    'reveal-from-top': bucketRevealDirection === 'top',
  });

  return (
    <div className={bucketContainerClassName} data-testid="color-buckets">
      {hasSiteAppearanceHexCodes ? renderTabs() : null}
      <input
        aria-label={I18n.t('select_color', { scope, color: activeColor })}
        onChange={(event) => onChangeActiveColor({ event, applyOnSixDigitRgbOnly: true })}
        onKeyUp={onKeyUpHexInput}
        type="text"
        value={activeColor ?? ''}
      />
      <div
        aria-label={I18n.t('color_swatches', { scope })}
        className={`color-buckets color-${palette.length}`}
        ref={colorBucketsRef}
        role="listbox"
      >
        {colors.map((color, index) => renderColorBucket(color, index))}
      </div>
    </div>
  );
};
